import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  COLOR_GREY_600,
  COLOR_PRIMARY_1_300,
  COLOR_PRIMARY_1_400,
  COLOR_PRIMARY_1_500,
  COLOR_PRIMARY_1_600,
  COLOR_PRIMARY_1_700,
  COLOR_PRIMARY_1_800,
  COLOR_PRIMARY_2_500,
  COLOR_PRIMARY_2_600,
  COLOR_PRIMARY_2_700,
  COLOR_PRIMARY_2_800,
  COLOR_PRIMARY_2_300,
  COLOR_PRIMARY_2_400,
} from 'src/utils/colors';

interface UsageData {
  grouped_usage_data: {
    [date: string]: {
      [endpoint: string]: number;
    };
  };
  total_usage_count: number;
  total_cost: number;
}

interface UsageByEndpointChartProps {
  data: UsageData;
  hasData?: boolean;
}

const UsageByEndpointChart: React.FC<UsageByEndpointChartProps> = ({ data }) => {
  const chartData = data;
  const hasChartData =
    chartData.grouped_usage_data && Object.keys(chartData.grouped_usage_data).length > 0;

  // Get all unique endpoints
  const allEndpoints = new Set<string>();
  Object.values(chartData.grouped_usage_data).forEach(dayData => {
    Object.keys(dayData).forEach(endpoint => allEndpoints.add(endpoint));
  });
  const endpoints = Array.from(allEndpoints);

  // Get all dates and sort them
  const dates = Object.keys(chartData.grouped_usage_data).sort();

  // Color palette for different endpoints
  const colors = [
    '#A8DADC',
    '#B7E4C7',
    '#FFF3B0',
    '#F7CAD0',
    '#CDB4DB',
    '#FFD6A5',
    '#A0C4FF',
    '#C8E7E0',
    '#FFADAD',
    '#D9D7F1',
    '#FFE5B4',
  ];

  // Create color map for unique endpoints
  const endpointColorMap = endpoints.reduce((map, endpoint, index) => {
    map[endpoint] = colors[index % colors.length];
    return map;
  }, {} as Record<string, string>);

  // Transform data for Recharts - only include endpoints with data
  const rechartsData = dates.map(date => {
    const dayData = chartData.grouped_usage_data[date];
    const formattedDate = new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

    const dataPoint: any = { date: formattedDate };
    endpoints.forEach(endpoint => {
      const value = dayData[endpoint] || 0;
      // Only include non-zero values
      if (value > 0) {
        dataPoint[endpoint] = value;
      }
    });

    return dataPoint;
  });

  return (
    <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%', p: 1 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '16px', mb: 3 }}>
          Usage by Endpoint
        </Typography>

        {!hasChartData ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '300px',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: COLOR_GREY_600,
                mb: 1,
                fontWeight: 500,
              }}
            >
              No usage data found
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: COLOR_GREY_600,
                maxWidth: '300px',
              }}
            >
              API usage will appear here once you start making requests
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={rechartsData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12, fill: COLOR_GREY_600 }}
                  axisLine={{ stroke: COLOR_GREY_600 }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: COLOR_GREY_600 }}
                  axisLine={{ stroke: COLOR_GREY_600 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '12px',
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px', color: COLOR_GREY_600 }} />
                {endpoints.map(endpoint => (
                  <Bar
                    key={endpoint}
                    dataKey={endpoint}
                    stackId="a"
                    fill={endpointColorMap[endpoint]}
                    name={endpoint}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default UsageByEndpointChart;
